import { Chip, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React from 'react';
import { useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import {
  APP_VERSION_PLATFORM_ICONS,
  APP_VERSION_PLATFORM_LABEL_MESSAGES,
} from '@constants/app-version';

import type { AppVersionSummaryFieldsFragment } from 'generated/app-version.generated';
import type { AppVersionApp } from 'generated/types';

import PlatformCard from './PlatformCard';

interface AppSectionProps {
  app: AppVersionApp;
  label: string;
  summaries: AppVersionSummaryFieldsFragment[];
}

function AppSection({ app, label, summaries }: AppSectionProps) {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();

  return (
    <Paper
      data-testid={`app-section-${app}`}
      elevation={0}
      sx={{
        border: `1px solid ${palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        alignItems="center"
        bgcolor="primary.main"
        direction="row"
        flexWrap="wrap"
        gap={1.5}
        justifyContent="space-between"
        px={2.5}
        py={1.75}
      >
        <Typography
          color="primary.contrastText"
          component="h2"
          fontWeight={700}
          variant="h5"
        >
          {label}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {summaries.map((summary) => (
            <Chip
              key={`${summary.app}-${summary.platform}`}
              data-testid={`app-platform-pill-${summary.app}-${summary.platform}`}
              icon={
                <Icon
                  color={palette.common.white}
                  name={APP_VERSION_PLATFORM_ICONS[summary.platform]}
                  type="fab"
                />
              }
              label={formatMessage(
                APP_VERSION_PLATFORM_LABEL_MESSAGES[summary.platform]
              )}
              size="small"
              sx={{
                backgroundColor: alpha(palette.common.white, 0.2),
                color: palette.common.white,
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: palette.common.white,
                  marginLeft: '8px',
                },
              }}
            />
          ))}
        </Stack>
      </Stack>

      <Stack spacing={2} p={2.5}>
        {summaries.map((summary) => (
          <PlatformCard
            key={`${summary.app}-${summary.platform}`}
            summary={summary}
          />
        ))}
      </Stack>
    </Paper>
  );
}

export default AppSection;
